import { useState, useCallback, useMemo } from 'react';
import { ConversionTableRule } from '@ab/ams-school-api/src/services/content/v1/ConversionTables/types';
import { RuleWithId, GradeSchemaInfo, GradePointMapping } from '~/types/gradeConversion';

interface UseGradeConversionRulesEditorProps {
  initialRules: ConversionTableRule[];
  gradeSchema: GradeSchemaInfo;
  destinationGradeType: 'numeric' | 'letter';
  gradePointMappings: GradePointMapping[];
}

export function useGradeConversionRulesEditor({
  initialRules,
  gradeSchema,
  destinationGradeType,
  gradePointMappings,
}: UseGradeConversionRulesEditorProps) {
  void gradePointMappings;
  const [rules, setRules] = useState<RuleWithId[]>(() =>
    initialRules.map((rule, index) => ({
      ...rule,
      tempId: `existing-${index}`,
    }))
  );

  const [nextTempId, setNextTempId] = useState(0);

  const isDirty = useMemo(() => {
    if (rules.length !== initialRules.length) return true;

    return rules.some((rule, index) => {
      const original = initialRules[index];
      if (!original) return true;

      return (
        rule.sourceRangeLow !== original.sourceRangeLow ||
        rule.sourceRangeHigh !== original.sourceRangeHigh ||
        rule.sourceLetterGrade !== original.sourceLetterGrade ||
        rule.destinationSingleGrade !== original.destinationSingleGrade ||
        rule.destinationLetterGrade !== original.destinationLetterGrade
      );
    });
  }, [rules, initialRules]);

  const validateAll = useCallback(() => true, []);

  const addRule = useCallback(() => {
    const newRule: RuleWithId = {
      tempId: `new-${nextTempId}`,
      ...(gradeSchema.gradeSchemaType === 'numeric'
        ? { sourceRangeLow: undefined, sourceRangeHigh: undefined }
        : { sourceLetterGrade: '' }),
      ...(destinationGradeType === 'numeric' ? { destinationSingleGrade: undefined } : { destinationLetterGrade: '' }),
    };

    setRules((prev) => [...prev, newRule]);
    setNextTempId((prev) => prev + 1);
  }, [nextTempId, gradeSchema.gradeSchemaType, destinationGradeType]);

  const removeRule = useCallback((index: number) => {
    setRules((prev) => prev.filter((_, i) => i !== index));
  }, []);

  const updateRule = useCallback((index: number, updates: Partial<ConversionTableRule>) => {
    setRules((prev) =>
      prev.map((rule, i) =>
        i === index
          ? {
              ...rule,
              ...updates,
            }
          : rule
      )
    );
  }, []);

  const resetRules = useCallback(() => {
    setRules(
      initialRules.map((rule, index) => ({
        ...rule,
        tempId: `existing-${index}`,
      }))
    );
    setNextTempId(0);
  }, [initialRules]);

  const getRulesForSubmission = useCallback((): ConversionTableRule[] => {
    return rules.map((rule) => sanitizeRuleForSubmission(rule, destinationGradeType));
  }, [destinationGradeType, rules]);

  return {
    rules,
    isDirty,
    addRule,
    removeRule,
    updateRule,
    resetRules,
    validateAll,
    getRulesForSubmission,
  };
}

function sanitizeRuleForSubmission(rule: RuleWithId, destinationGradeType: 'numeric' | 'letter'): ConversionTableRule {
  const { tempId, ...rest } = rule;
  void tempId;
  const sanitized: ConversionTableRule = {};

  const assignIfProvided = <K extends keyof ConversionTableRule>(key: K, value: ConversionTableRule[K]) => {
    if (value !== undefined && value !== null && value !== '') {
      sanitized[key] = value;
    }
  };

  assignIfProvided('sourceRangeLow', rest.sourceRangeLow);
  assignIfProvided('sourceRangeHigh', rest.sourceRangeHigh);
  assignIfProvided('sourceLetterGrade', rest.sourceLetterGrade);
  assignIfProvided('destinationRangeLow', rest.destinationRangeLow);
  assignIfProvided('destinationRangeHigh', rest.destinationRangeHigh);

  if (destinationGradeType === 'numeric') {
    assignIfProvided('destinationSingleGrade', rest.destinationSingleGrade);
  } else {
    assignIfProvided('destinationLetterGrade', rest.destinationLetterGrade);
  }

  return sanitized;
}
